import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Select } from 'antd';
import { useSelector } from 'react-redux';

import FormTextInput from '../common/inputs/FormTextInput';
import Permissions from '../auth/Permissions';

import { CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE, CONTACT_ADDRESS_BOOK_SLIDER_EDIT_MODE, CONTACT_ADDRESS_BOOK_SLIDER_VIEW_MODE } from './contactConstants';
import { getConnectionOptions } from './contactHelpers';

/** Contact Address Book Form Component */
const ContactAddressBookForm = ({
  form,
  mode,
  showConnection = false,
  divisions = [],
  selected: {
    name,
    role,
    phoneNumber,
    email,
  },
}) => {
  const customerMap = useSelector((state) => state.customers.customers);
  const vendorMap = useSelector((state) => state.vendors.vendors);
  const canSeeCustomers = Permissions.match('CUSTOMERS');
  const canSeeVendors = Permissions.match('VENDORS');

  const canSelectConnection = showConnection && (canSeeCustomers || canSeeVendors);

  const customerOptions = useMemo(() => (
    canSeeCustomers ? getConnectionOptions(customerMap, "customer", divisions) : []
  ), [canSeeCustomers, customerMap, divisions]);

  const vendorOptions = useMemo(() => (
    canSeeVendors ? getConnectionOptions(vendorMap, "vendor", divisions) : []
  ), [canSeeVendors, vendorMap, divisions]);

  const isNotDisplay = mode === CONTACT_ADDRESS_BOOK_SLIDER_EDIT_MODE
    || mode === CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE;
  return (
    <Form
      form={form}
      layout="vertical"
      style={{ width: '100%' }}
    >
      <FormTextInput
        isNotDisplay={isNotDisplay}
        label="Name"
        name="name"
        value={name}
        rules={[{ required: true, message: 'Please input contact name' }]}
      />
      <FormTextInput
        isNotDisplay={isNotDisplay}
        label="Role"
        name="role"
        value={role}
      />
      <FormTextInput
        isNotDisplay={isNotDisplay}
        label="Phone Number"
        name="phoneNumber"
        value={phoneNumber}
      />
      <FormTextInput
        isNotDisplay={isNotDisplay}
        label="Email"
        name="email"
        value={email}
        rules={[{ type: 'email', message: 'Invalid email format' }]}
      />
      {isNotDisplay && canSelectConnection && (
        <Form.Item
          label="Connection"
          name="connection"
          style={{
            marginTop: 10,
          }}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Select a customer or vendor"
          >
            {customerOptions.length > 0 && (
              <Select.OptGroup key="customers" label="Customers">
                {customerOptions.map((option) => (
                  <Select.Option value={option.value} key={option.value} label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
            {vendorOptions.length > 0 && (
              <Select.OptGroup key="vendors" label="Vendors">
                {vendorOptions.map((option) => (
                  <Select.Option value={option.value} key={option.value} label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </Select>
        </Form.Item>
      )}
    </Form>
  );
};

ContactAddressBookForm.propTypes = {
  form: PropTypes.object.isRequired,
  mode: PropTypes.oneOf([
    CONTACT_ADDRESS_BOOK_SLIDER_VIEW_MODE,
    CONTACT_ADDRESS_BOOK_SLIDER_EDIT_MODE,
    CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE,
  ]),
  showConnection: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.object,
};

ContactAddressBookForm.defaultProps = {
  mode: CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE,
  showConnection: false,
  divisions: [],
  selected: {},
};

export default ContactAddressBookForm;
